<template>
  <div class="dashboard-panel">
    <div class="btn-toolbar" style="gap: 8px; margin-bottom: 12px; margin-left: -15px; align-items: center;">
      <template v-if="!editMode">
        <button type="button" class="btn btn-primary" @click="loadAll()">
          <font-awesome-icon icon="history" /> &nbsp; {{ $t('dashboardPanel.refresh') }}
        </button>
        <button type="button" class="btn btn-outline-secondary" @click="startEdit()">
          <font-awesome-icon icon="edit" /> &nbsp; {{ $t('dashboardPanel.edit') }}
        </button>
      </template>
      <template v-else>
        <button type="button" class="btn btn-primary" @click="openTileModal(null)">
          <font-awesome-icon icon="plus" /> &nbsp; {{ $t('dashboardPanel.addTile') }}
        </button>
        <button type="button" class="btn btn-success" @click="saveAsUser()">
          <font-awesome-icon icon="check" /> &nbsp; {{ $t('dashboardPanel.save') }}
        </button>
        <button type="button" v-if="userIsAdmin" class="btn btn-outline-secondary" @click="saveAsOrgDefault()">
          {{ $t('dashboardPanel.saveAsOrgDefault') }}
        </button>
        <button type="button" v-if="source === 'user'" class="btn btn-outline-secondary" @click="resetOwn()">
          {{ $t('dashboardPanel.resetOwn') }}
        </button>
        <button type="button" class="btn btn-secondary" @click="cancelEdit()">
          {{ $t('dashboardPanel.cancel') }}
        </button>
      </template>
      <span v-if="isLoadingPanel"><span class="spinner-border spinner-border-sm"></span> {{ $t('dashboardPanel.loading') }}</span>
      <span v-if="!editMode && source === 'organization'" class="source-badge" v-title="$t('dashboardPanel.orgDefaultHint')">
        {{ $t('dashboardPanel.orgDefaultBadge') }}
      </span>
      <span v-if="saveMessage" class="save-message">{{ saveMessage }}</span>
    </div>

    <div v-if="loaded && tiles.length === 0" class="empty-state">
      <p>{{ $t('dashboardPanel.empty') }}</p>
      <p class="empty-hint">{{ $t('dashboardPanel.emptyHint') }}</p>
    </div>

    <div class="tile-grid">
      <div v-for="(tile, index) in tiles" :key="tile.id" class="tile-card" :class="{ 'tile-editing': editMode, 'tile-clickable': !editMode && hasDetail(tile) }" :style="tileBorderStyle(tile)" @click="editMode ? openTileModal(tile) : openDetailModal(tile)">
        <div v-if="editMode" class="tile-edit-controls" @click.stop>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="index === 0" @click="moveTile(index, -1)" v-title="$t('dashboardPanel.moveUp')">
            <font-awesome-icon icon="arrow-up" />
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="index === tiles.length - 1" @click="moveTile(index, 1)" v-title="$t('dashboardPanel.moveDown')">
            <font-awesome-icon icon="arrow-down" />
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeTile(index)" v-title="$t('dashboardPanel.removeTile')">
            <font-awesome-icon icon="trash" />
          </button>
        </div>
        <div class="tile-title">{{ tile.title || defaultTitle(tile) }}</div>

        <!-- broken reference (deleted sensor etc.) -->
        <template v-if="tileError(tile)">
          <div class="tile-error">{{ $t('dashboardPanel.tileError') }}</div>
          <div class="tile-sub">{{ tileError(tile) }}</div>
        </template>

        <!-- momentary tile: fed live from the 60s monitoring poll -->
        <template v-else-if="tile.kind === 'momentary'">
          <div class="tile-value" v-if="momentary(tile)">
            {{ $formatNumber(momentary(tile).value, 2) }}
            <span class="tile-unit">{{ unitFor(tile.valuetype) }}</span>
          </div>
          <div class="tile-value tile-pending" v-else>&mdash;</div>
          <div class="tile-sub" v-if="momentary(tile)">{{ fromNow(momentary(tile).recordedtime) }}</div>
        </template>

        <!-- status tile: monitoring health, computed client-side from the
             same latest-data tree (shared utils/monitoring-status module) -->
        <template v-else-if="tile.kind === 'status'">
          <template v-if="statusMissing(tile)">
            <div class="tile-error">{{ $t('dashboardPanel.tileError') }}</div>
            <div class="tile-sub">{{ $t('dashboardPanel.statusSourceMissing') }}</div>
          </template>
          <template v-else-if="statusEntries(tile)">
            <div class="status-summary">
              <span v-if="statusSummaryFor(tile).critical > 0" class="status-count"><span class="status-dot" :style="dotStyle(3)"></span>{{ statusSummaryFor(tile).critical }} {{ $t('dashboardPanel.statusCritical') }}</span>
              <span v-if="statusSummaryFor(tile).warning > 0" class="status-count"><span class="status-dot" :style="dotStyle(2)"></span>{{ statusSummaryFor(tile).warning }} {{ $t('dashboardPanel.statusWarning') }}</span>
              <span v-if="statusSummaryFor(tile).info > 0" class="status-count"><span class="status-dot" :style="dotStyle(1)"></span>{{ statusSummaryFor(tile).info }} {{ $t('dashboardPanel.statusInfo') }}</span>
              <span v-if="statusSummaryFor(tile).ok > 0" class="status-count"><span class="status-dot" :style="dotStyle(0)"></span>{{ statusSummaryFor(tile).ok }} {{ $t('dashboardPanel.statusOk') }}</span>
              <span v-if="statusSummaryFor(tile).disabled > 0" class="status-count status-muted"><span class="status-dot status-dot-disabled" :style="dotStyle(4)"></span>{{ statusSummaryFor(tile).disabled }} {{ $t('dashboardPanel.statusDisabled') }}</span>
            </div>
            <div class="status-entries">
              <div v-for="entry in flaggedStatusEntries(tile)" :key="entry.key" class="status-entry">
                <span class="status-dot" :style="dotStyle(entry.severity)"></span>
                <span class="status-entry-name">{{ entry.name }}</span>
              </div>
              <div v-if="statusMoreCount(tile) > 0" class="status-more">{{ $t('dashboardPanel.statusMore', { count: statusMoreCount(tile) }) }}</div>
              <div v-if="flaggedStatusEntries(tile).length === 0" class="status-all-ok">{{ $t('dashboardPanel.statusAllOk') }}</div>
            </div>
          </template>
          <div class="tile-value tile-pending" v-else>&mdash;</div>
        </template>

        <!-- period tile: sums + comparison from /api/dashboard/data -->
        <template v-else>
          <div class="tile-value" v-if="tileData(tile) && tileData(tile).current">
            {{ tileData(tile).current.sum != null ? $formatNumber(tileData(tile).current.sum, 2) : '—' }}
            <span class="tile-unit">{{ tileData(tile).unit }}</span>
          </div>
          <div class="tile-value tile-pending" v-else>&mdash;</div>
          <div class="tile-sub">{{ $t('dashboardPanel.period.' + tile.period) }}</div>
          <template v-if="tileData(tile)">
            <div class="tile-compare" v-if="changeDisplay(tile)"
                 :class="tileData(tile).changePercent > 0 ? 'worse' : 'better'">
              {{ changeDisplay(tile) }} {{ $t('dashboardPanel.compare.' + tile.period) }}
            </div>
            <div class="mini-bars" v-title="$t('dashboardPanel.miniBarsHint')">
              <div class="mini-bar-row">
                <span class="mini-bar-label">{{ $t('dashboardPanel.currentShort') }}</span>
                <div class="mini-bar-track"><div class="mini-bar current" :style="{ width: miniBars(tile).current + '%' }"></div></div>
              </div>
              <div class="mini-bar-row">
                <span class="mini-bar-label">{{ $t('dashboardPanel.previousShort') }}</span>
                <div class="mini-bar-track"><div class="mini-bar previous" :style="{ width: miniBars(tile).previous + '%' }"></div></div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <!-- Teleport to body: nested inside this component the Bootstrap
         backdrop (appended to body) stacks ABOVE the modal and swallows
         every click — the app's other modals live at HomePage root level
         for the same reason. -->
    <Teleport to="body">
      <edit-dashboard-tile-modal
        @modalClosed="tileModalClosed"
        @saveTile="applyTile"
        :editTile="tileBeingEdited"
        action="edit"
        :modalOpened="tileModalTimestamp" />

      <dashboard-tile-detail-modal
        @modalClosed="detailModalClosed"
        :tile="tileInDetail"
        :detailData="tileInDetail ? tileData(tileInDetail) : null"
        action="show"
        :modalOpened="detailModalTimestamp" />

      <dashboard-status-detail-modal
        @modalClosed="statusModalClosed"
        @openEntry="jumpToFacility"
        :tile="statusTileInDetail"
        :entries="statusTileInDetail ? statusEntries(statusTileInDetail) : null"
        action="show"
        :modalOpened="statusModalTimestamp" />
    </Teleport>
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import { mapGetters } from 'vuex'
import dashboardService from '@/services/dashboard'
import EditDashboardTileModal from '@/modals/EditDashboardTileModal.vue'
import DashboardTileDetailModal from '@/modals/DashboardTileDetailModal.vue'
import DashboardStatusDetailModal from '@/modals/DashboardStatusDetailModal.vue'
import { momentaryValueForTile, formatChangePercent, miniBarWidths, sensorForTile } from '@/utils/dashboard'
import { statusForDevice, statusForFacility, statusSummary, colorForSeverity } from '@/utils/monitoring-status'
import moment from 'moment'

//
// Dashboard tab content. HomePage only hosts the tab button — everything
// dashboard lives here (the monolith is big enough).
//
// Momentary tiles read the latest-data tree the monitoring poll refreshes
// every 60 s; period tiles are computed server-side (POST /api/dashboard/data)
// and refreshed via the button (daily totals move slowly).
//
export default {
  name: 'DashboardPanel',
  components: {
    'edit-dashboard-tile-modal': EditDashboardTileModal,
    'dashboard-tile-detail-modal': DashboardTileDetailModal,
    'dashboard-status-detail-modal': DashboardStatusDetailModal
  },
  emits: ['openFacility'],
  data () {
    return {
      loaded: false,
      isLoadingPanel: false,
      source: 'empty',
      config: { version: 1, tiles: [] },
      tileDataById: {},
      editMode: false,
      configBeforeEdit: null,
      tileBeingEdited: null,
      tileModalTimestamp: '',
      tileInDetail: null,
      detailModalTimestamp: '',
      statusTileInDetail: null,
      statusModalTimestamp: '',
      saveMessage: ''
    }
  },
  computed: {
    ...mapGetters(['organizations', 'client']),
    tiles () {
      return this.config && Array.isArray(this.config.tiles) ? this.config.tiles : []
    },
    userIsAdmin () {
      return this.client && this.client.role === 4
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    loadAll () {
      this.isLoadingPanel = true
      dashboardService.getDashboard().then(data => {
        this.source = data.source
        try {
          this.config = typeof data.config === 'string' ? JSON.parse(data.config) : data.config
        } catch (e) {
          this.config = { version: 1, tiles: [] }
        }
        this.loaded = true
        this.loadPeriodData()
      }).catch(() => {
        this.loaded = true
        this.isLoadingPanel = false
      })
    },
    loadPeriodData () {
      const periodTiles = this.tiles.filter(t => t.kind === 'period')
      if (periodTiles.length === 0) {
        this.isLoadingPanel = false
        return
      }
      dashboardService.getTileData(periodTiles).then(data => {
        this.tileDataById = data.tiles || {}
        this.isLoadingPanel = false
      }).catch(() => {
        this.isLoadingPanel = false
      })
    },
    tileData (tile) {
      return this.tileDataById[tile.id] || null
    },
    tileError (tile) {
      const data = this.tileData(tile)
      return data && data.error ? data.error : null
    },
    momentary (tile) {
      return momentaryValueForTile(this.organizations, tile)
    },
    miniBars (tile) {
      const data = this.tileData(tile)
      if (!data || !data.current) return { current: 0, previous: 0 }
      return miniBarWidths(data.current.sum, data.previous ? data.previous.sum : null)
    },
    changeDisplay (tile) {
      const data = this.tileData(tile)
      return data ? formatChangePercent(data.changePercent) : null
    },
    unitFor (valuetype) {
      const unit = this.$t('physicalUnit.' + valuetype)
      return unit && !unit.startsWith('physicalUnit.') ? unit : ''
    },
    defaultTitle (tile) {
      if (tile.kind === 'status') {
        if (tile.statusScope === 'facility') {
          const facility = this.findFacilityById(tile.facilityId)
          return this.$t('dashboardPanel.statusTitlePrefix') + ' — ' + (facility ? facility.name : '#' + tile.facilityId)
        }
        return this.$t('dashboardPanel.statusTitlePrefix') + ' — ' + this.$t('dashboardPanel.statusScopeOrganization')
      }
      const resolved = sensorForTile(this.organizations, tile)
      return resolved && resolved.sensor && resolved.sensor.name ? resolved.sensor.name : '#' + (tile.sensorId || tile.collectionAddress)
    },
    fromNow (recordedtime) {
      return recordedtime ? moment(recordedtime).fromNow() : ''
    },

    // ---- status tiles ----------------------------------------------

    findFacilityById (facilityId) {
      for (const organization of this.organizations || []) {
        for (const facility of organization.facilities || []) {
          if (facility.id === facilityId) {
            return facility
          }
        }
      }
      return null
    },
    // entry list of a status tile, or null while the latest-data poll has
    // not filled the tree yet (renders as "—", like momentary tiles)
    statusEntries (tile) {
      if (!this.organizations || this.organizations.length === 0) return null
      if (tile.statusScope === 'facility') {
        const facility = this.findFacilityById(tile.facilityId)
        if (!facility) return null
        return (facility.devices || []).map(device => ({
          key: 'd' + device.id,
          name: device.virtual === true ? this.$t('deviceNames.virtual') : (device.name || device.apikey),
          facilityId: facility.id,
          ...statusForDevice(device)
        }))
      }
      const organization = this.organizations[0]
      return (organization.facilities || []).map(facility => ({
        key: 'f' + facility.id,
        name: facility.name,
        facilityId: facility.id,
        ...statusForFacility(facility)
      }))
    },
    // facility referenced by the tile no longer visible for this user
    statusMissing (tile) {
      return tile.statusScope === 'facility' &&
        this.organizations && this.organizations.length > 0 &&
        !this.findFacilityById(tile.facilityId)
    },
    statusSummaryFor (tile) {
      return statusSummary(this.statusEntries(tile) || [])
    },
    flaggedStatusEntries (tile) {
      const entries = (this.statusEntries(tile) || [])
        .filter(entry => entry.severity >= 1 && entry.severity <= 3)
        .sort((a, b) => b.severity - a.severity)
      return entries.slice(0, 5)
    },
    statusMoreCount (tile) {
      const flagged = (this.statusEntries(tile) || []).filter(entry => entry.severity >= 1 && entry.severity <= 3)
      return Math.max(0, flagged.length - 5)
    },
    dotStyle (severity) {
      return { backgroundColor: colorForSeverity(severity) }
    },
    // worst status of the tile as a coloured left border (alert-mail pattern)
    tileBorderStyle (tile) {
      if (tile.kind !== 'status') return null
      const entries = this.statusEntries(tile)
      if (!entries || this.statusMissing(tile)) return null
      return { borderLeft: '6px solid ' + colorForSeverity(statusSummary(entries).worst) }
    },
    jumpToFacility (facilityId) {
      const el = document.getElementById('dashboardStatusDetailModal')
      if (el) {
        Modal.getInstance(el)?.hide()
      }
      this.$emit('openFacility', facilityId)
    },

    // ---- edit mode -------------------------------------------------

    startEdit () {
      this.configBeforeEdit = JSON.stringify(this.config)
      this.editMode = true
      this.saveMessage = ''
    },
    cancelEdit () {
      if (this.configBeforeEdit) {
        this.config = JSON.parse(this.configBeforeEdit)
      }
      this.editMode = false
      this.tileBeingEdited = null
    },
    moveTile (index, delta) {
      const target = index + delta
      if (target < 0 || target >= this.config.tiles.length) return
      const tiles = this.config.tiles
      const moved = tiles.splice(index, 1)[0]
      tiles.splice(target, 0, moved)
    },
    removeTile (index) {
      this.config.tiles.splice(index, 1)
    },
    openTileModal (tile) {
      this.tileBeingEdited = tile
      this.tileModalTimestamp = new Date().toISOString()
      const el = document.querySelector('#editDashboardTileModal')
      Modal.getOrCreateInstance(el, { backdrop: 'static' }).show()
    },
    tileModalClosed () {
      this.tileBeingEdited = null
    },
    hasDetail (tile) {
      if (tile.kind === 'status') {
        return !this.statusMissing(tile) && !!this.statusEntries(tile)
      }
      const data = this.tileData(tile)
      return tile.kind === 'period' && data && !data.error
    },
    openDetailModal (tile) {
      if (!this.hasDetail(tile)) return
      if (tile.kind === 'status') {
        this.statusTileInDetail = tile
        this.statusModalTimestamp = new Date().toISOString()
        const el = document.querySelector('#dashboardStatusDetailModal')
        Modal.getOrCreateInstance(el, { backdrop: 'static' }).show()
        return
      }
      this.tileInDetail = tile
      this.detailModalTimestamp = new Date().toISOString()
      const el = document.querySelector('#dashboardTileDetailModal')
      Modal.getOrCreateInstance(el, { backdrop: 'static' }).show()
    },
    detailModalClosed () {
      this.tileInDetail = null
    },
    statusModalClosed () {
      this.statusTileInDetail = null
    },
    applyTile (tile) {
      const index = this.config.tiles.findIndex(t => t.id === tile.id)
      if (index >= 0) {
        this.config.tiles.splice(index, 1, tile)
      } else {
        this.config.tiles.push(tile)
      }
    },
    saveAsUser () {
      this.persist(dashboardService.saveUserDashboard(this.config), 'user')
    },
    saveAsOrgDefault () {
      if (!window.confirm(this.$t('dashboardPanel.confirmOrgSave'))) return
      this.persist(dashboardService.saveOrganizationDashboard(this.config), 'organization')
    },
    resetOwn () {
      if (!window.confirm(this.$t('dashboardPanel.confirmResetOwn'))) return
      this.isLoadingPanel = true
      dashboardService.deleteUserDashboard().then(() => {
        this.editMode = false
        this.loadAll()
      }).catch((error) => {
        this.isLoadingPanel = false
        this.saveMessage = error.message
      })
    },
    persist (promise, savedAs) {
      this.isLoadingPanel = true
      promise.then(() => {
        this.editMode = false
        this.source = savedAs
        this.saveMessage = this.$t('dashboardPanel.saved')
        setTimeout(() => { this.saveMessage = '' }, 4000)
        this.loadPeriodData()
      }).catch((error) => {
        this.isLoadingPanel = false
        this.saveMessage = error.message
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-left: -15px;
  margin-right: -15px;
}

@media (min-width: 729px) {
  .tile-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.tile-card {
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #ced4da);
  border-radius: 0.5rem;
  padding: 12px 14px;
  min-height: 120px;
}

/* dark theme: lift the cards off the page background, same surface tone
   as the app's forms (.public .form in App.vue) */
[data-bs-theme="dark"] .tile-card {
  background: #2b2b2b;
  border-color: #555;
}

.tile-editing {
  cursor: pointer;
  border-style: dashed;
}

.tile-clickable {
  cursor: pointer;
}

.tile-clickable:hover {
  border-color: #6695c2;
  box-shadow: 0 1px 4px rgba(102, 149, 194, 0.3);
}

.tile-edit-controls {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.save-message {
  font-size: 0.85em;
  color: #2e8540;
}

.tile-title {
  font-weight: 600;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tile-value {
  font-size: 1.6em;
  font-weight: 700;
}

.tile-pending {
  color: #aaa;
}

.tile-unit {
  font-size: 0.55em;
  font-weight: 400;
  color: #6c757d;
}

.tile-sub {
  font-size: 0.8em;
  color: #6c757d;
}

.tile-compare {
  font-size: 0.85em;
  font-weight: 600;
  margin-top: 4px;
}

/* consumption up = bad, down = good */
.tile-compare.worse { color: #ce2029; }
.tile-compare.better { color: #2e8540; }

.tile-error {
  color: #ce2029;
  font-weight: 600;
}

.mini-bars {
  margin-top: 8px;
}

.mini-bar-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
}

.mini-bar-label {
  font-size: 0.7em;
  color: #6c757d;
  width: 52px;
  flex-shrink: 0;
}

.mini-bar-track {
  flex: 1;
  background: #f0f2f5;
  border-radius: 3px;
  height: 8px;
}

[data-bs-theme="dark"] .mini-bar-track {
  background: #3a3f45;
}

[data-bs-theme="dark"] .tile-unit,
[data-bs-theme="dark"] .tile-sub,
[data-bs-theme="dark"] .mini-bar-label,
[data-bs-theme="dark"] .empty-state {
  color: #adb5bd;
}

[data-bs-theme="dark"] .source-badge {
  background: #2b3a4a;
  color: #a9c4de;
  border-color: #4a6b8a;
}

.mini-bar {
  height: 8px;
  border-radius: 3px;
}

.mini-bar.current { background: #6695c2; }
.mini-bar.previous { background: #c3cede; }

.status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 0.85em;
  margin-bottom: 8px;
}

.status-count {
  white-space: nowrap;
}

.status-muted {
  color: #6c757d;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: baseline;
}

/* the light grey needs an outline to stay visible on white cards */
.status-dot-disabled {
  border: 1px solid #ced4da;
}

.status-entry {
  font-size: 0.9em;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-more {
  font-size: 0.8em;
  color: #6c757d;
  margin-top: 2px;
}

.status-all-ok {
  font-size: 0.9em;
  color: #2e8540;
}

[data-bs-theme="dark"] .status-more,
[data-bs-theme="dark"] .status-muted {
  color: #adb5bd;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 40px 0;
}

.empty-hint {
  font-size: 0.85em;
}

.source-badge {
  font-size: 0.75em;
  background: #eef3f8;
  color: #4a6b8a;
  border: 1px solid #c3cede;
  border-radius: 10px;
  padding: 2px 10px;
}
</style>
